'use strict';

/* eslint-disable no-console,no-undefined */

/**
 * @module
 * Knotty
 * @description
 * A JS module for posting client-side exceptions and developer-authored
 * messages to the server.
 */

const debounce = require('lodash.debounce');

const createLog = require('./utils/createLog');
const postLogs = debounce(require('./utils/postLogs'), 1000);


/**
 * @name buildKnotty
 * @description
 * A tool for posting client side exception logs and developer-authored info/error logs to the
 * server. Returns an object with methods for posting exceptions, errors, and info logs.
 * @param {object=} [options]
 * @param {string=} [options.errorRoute=/errors] - Route to post exception logs
 * @param {string=} [options.clientLogRoute=/cl] - Route to post info/error level logs
 * @param {function=} [options.getCsrfToken] - Method to be used client-side for getting the current CSRF token
 * @param {function=} [options.setCsrfToken] - Method to be used client-side for setting the current CSRF token
 * @return {object} Object with Knotty methods
 *
 * @example
 * ```js
 * const buildKnotty = require('knotty');
 * const knotty = buildKnotty({
 *     errorRoute: '/careers/errors',
 *     clientLogRoute: '/careers/cl',
 * });
 * // Functions for posting info/error level logs are available on knotty.
 * knotty.logError('There\'s a snake in my boot!');
 * knotty.logInfo('I\'m a cowboy! Howdy Howdy Howdy');
 * // The module version of Knotty doesn't attach an exception handler to `window.onerror`
 * // automatically. To match the behavior of the browser script, do
 * window.onerror = knotty.logException;
 * ```
 */

function buildKnotty(options = {}) {
    const logs = {
        exception: [],
        client: []
    };

    options.errorRoute = options.errorRoute || '/errors';
    options.clientLogRoute = options.clientLogRoute || '/cl';

    options.getCsrfToken = options.getCsrfToken || function() { // eslint-disable-line consistent-return
        if (window.ha) {
            return window.ha.csrfToken;
        }
    };
    options.setCsrfToken = options.setCsrfToken || function(newToken) {
        if (window.ha) {
            window.ha.csrfToken = newToken;
        }
    };

    /**
     * @name
     * makeLogger
     * @private
     * @description
     * Returns a function for logging errors and client messages to the server.
     * @param {string} route - Where should logs be posted?
     * @param {array} innerLogs - A reference to an array of logs to be posted
     * @param {string} logType - What type of log is this? ex: 'exception', 'info', 'error'.
     *                           Logs of type 'exception' are treated as if they were
     *                           created by window.onerror.  Other log types are considered to
     *                           be created by the developer.
     * @return {function}
     */

    function makeLogger(route, innerLogs, logType) {
        return function knottyPostLogs(...args) {
            // For developer-authored logs, one argument (message) is required.
            if (logType !== 'exception' && !args[0]) {
                throw new Error('[knotty] A message is required');
            }
            innerLogs.push(createLog(args, logType));
            postLogs(route, innerLogs, logType, options, function postLogsCallback(err) {
                if (err) {
                    console.log(`[knotty] There was a problem posting ${logType} logs to ${route}`);
                }
                innerLogs = [];
                return;
            });
        };
    }

    return {
        /**
         * @name
         * knotty#logException
         * @description
         * A function for posting caught exceptions to the server
         * @param {string} message
         */

        logException: makeLogger(options.errorRoute, logs.exception, 'exception'),

        /**
         * @name
         * knotty#logError
         * @description
         * A function for posting developer-generated error logs to the server.
         * @param {string} message
         */

        logError: makeLogger(options.clientLogRoute, logs.client, 'error'),

        /**
         * @name
         * knotty#logInfo
         * @description
         * A function for posting developer-generated info logs to the server.
         * @param {string} message
         */

        logInfo: makeLogger(options.clientLogRoute, logs.client, 'info')
    };
}

module.exports = buildKnotty;
